import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";
import { QRCodeCanvas } from "qrcode.react";
import { eInvoiceTableAction } from "../../../redux/actions/sales-action";
import LoadingSpinner from "../../helper-components/loading-spinner";
import { FieldItem, Section } from "./create-e-invoice";
import EInvoiceStatusBadge from "./e-invoice-status-badge";
import { getEInvoiceList, getRowId } from "./e-invoice-utils";

/* Rows fetched from the list API when the page is opened directly (no row in router state) */
const FALLBACK_PAGE_SIZE = 100;

/* QR codes above this length can't be encoded; such values are shown as text instead */
const MAX_QR_LENGTH = 2500;

const YES_NO = { Y: "Yes", N: "No" };

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/* "sellerdtls_Gstin" / "SellerDtls.Gstin" → "sellerdtlsgstin" */
const normalizeKey = (k) => String(k).toLowerCase().replace(/[^a-z0-9]/g, "");

/*
 * irnDetails comes back flat (trandtls_taxsch, sellerdtls_gstin, doc_no …).
 * Returns get(...candidates) that looks a field up case/underscore-insensitively.
 */
const makeGetter = (obj) => {
  const map = {};
  if (isPlainObject(obj)) {
    Object.entries(obj).forEach(([k, v]) => {
      map[normalizeKey(k)] = v;
    });
  }
  return (...candidates) => {
    for (const c of candidates) {
      const v = map[normalizeKey(c)];
      if (v !== undefined && v !== null && v !== "") return v;
    }
    return null;
  };
};

/* Item list may be an array or a JSON string under itemlist / items */
const findItems = (obj) => {
  if (!isPlainObject(obj)) return [];
  const key = Object.keys(obj).find((k) => /^(itemlist|items?)$/.test(normalizeKey(k)));
  let value = key ? obj[key] : null;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      value = null;
    }
  }
  return Array.isArray(value) ? value : [];
};

const findSignedQr = (obj) => {
  if (!isPlainObject(obj)) return null;
  const key = Object.keys(obj).find((k) => /signedqrcode/.test(normalizeKey(k)));
  return key ? obj[key] : null;
};

const formatDateTime = (v) =>
  v && moment(v).isValid() ? moment(v).format("MMM D, YYYY, hh:mm A") : v ?? null;

const joinAddress = (...parts) => parts.filter(Boolean).join(", ") || null;

/*
 * e-Invoice list row for soit_id: taken from router state (row clicked in the list),
 * otherwise looked up in the list API (page opened directly / refreshed).
 */
export const useEInvoiceRow = (invoiceId) => {
  const location = useLocation();
  const dispatch = useDispatch();

  const passedRow = location.state?.eInvoice ?? null;
  const [row, setRow] = useState(passedRow);
  const [loading, setLoading] = useState(!passedRow);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (passedRow) return;
    setLoading(true);
    dispatch(
      eInvoiceTableAction(
        { pageNumber: 1, pageSize: FALLBACK_PAGE_SIZE, filterData: {} },
        (data) => {
          const match = getEInvoiceList(data).find((r) => getRowId(r) === invoiceId) ?? null;
          setRow(match);
          setNotFound(!match);
          setLoading(false);
        },
        () => {
          setNotFound(true);
          setLoading(false);
        }
      )
    );
  }, [invoiceId]);

  return { row, loading, notFound: notFound || (!loading && !row) };
};

export const EInvoiceNotFound = () => {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center gap-3 h-full text-[var(--secondary)]" style={{ fontSize: "14px" }}>
      <span>e-Invoice details could not be found for this invoice.</span>
      <button
        type="button"
        onClick={() => navigate("/e-invoice")}
        className="px-[24px] h-[40px] rounded-md border border-[var(--draft-button-border)] text-[var(--draft-button-fg)] bg-[var(--draft-button-bg)]"
      >
        Back to e-Invoice list
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
const ViewEInvoice = ({ invoiceId }) => {
  const { row, loading, notFound } = useEInvoiceRow(invoiceId);

  if (loading) return <LoadingSpinner />;
  if (notFound) return <EInvoiceNotFound />;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <EInvoiceDetails row={row} />
      </div>
    </div>
  );
};

/* Read-only e-Invoice summary + details (same layout as the push screen) */
export const EInvoiceDetails = ({ row }) => {
  const inv = row?.sales_order ?? row;
  const status = row?.eInvoiceStatus ?? {};
  const irnDetails = row?.irnDetails ?? null;
  const get = makeGetter(irnDetails);

  const irn = status?.irn ?? get("irn");
  const signedQr = findSignedQr(irnDetails) ?? findSignedQr(row);
  const rawDate = inv?.soit_order_date ?? inv?.created_date;
  const items = findItems(irnDetails);

  /* section: trandtls / sellerdtls / buyerdtls / shipdtls / dispdtls / valdtls */
  const field = (section, name) => get(`${section}_${name}`, `${section}${name}`);
  const yesNo = (v) => (v ? YES_NO[v] ?? v : null);

  const hasShip = ["gstin", "lglnm", "addr1", "loc", "pin", "stcd"].some((f) => field("shipdtls", f));
  const hasDisp = ["nm", "addr1", "loc", "pin", "stcd"].some((f) => field("dispdtls", f));

  const optional = (label, value) => (value ? <FieldItem label={label} value={value} /> : null);

  return (
    <>
        {/* ── SUMMARY ──────────────────────────────────────────────────────── */}
        <Section title="e-Invoice Summary">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <FieldItem label="Transaction #" value={inv?.soit_invoice_number} />
              <FieldItem
                label="Date"
                value={rawDate && moment(rawDate).isValid() ? moment(rawDate).format("MMM D, YYYY") : null}
              />
              <FieldItem label="Customer Name" value={inv?.cmr_name} />
              <FieldItem
                label="Total"
                value={inv?.soit_total_amount != null ? `₹ ${inv.soit_total_amount}` : null}
              />
              <div className="flex flex-col gap-[4px] min-w-0">
                <span className="text-[var(--input-label-color)] text-[length:var(--input-label-font-size)] font-[var(--input-label-font-weight)]">
                  Status
                </span>
                <div style={{ minHeight: "36px", display: "flex", alignItems: "center" }}>
                  <EInvoiceStatusBadge status={status?.status} />
                </div>
              </div>
              <FieldItem label="Ack No" value={status?.ack_no} />
              <FieldItem label="Ack Date" value={formatDateTime(status?.ack_date)} />
              {status?.can_cancel && (
                <FieldItem label="Cancel Before" value={formatDateTime(status?.cancellation_deadline)} />
              )}
              <div className="sm:col-span-2 lg:col-span-4 min-w-0">
                <FieldItem label="IRN – Invoice Reference Number" value={irn} singleLine />
              </div>
              {optional("Cancellation Reason", status?.cancellation_reason)}
              {optional("Cancellation Remark", status?.cancellation_remark)}
              {optional("Blocked Reason", status?.blocked_reason)}
            </div>

            {signedQr && (
              <div className="flex flex-col items-center gap-2 shrink-0">
                <span className="text-[var(--input-label-color)]" style={{ fontSize: "13px" }}>
                  Signed QR Code
                </span>
                {String(signedQr).length <= MAX_QR_LENGTH ? (
                  <QRCodeCanvas value={String(signedQr)} size={180} level="L" />
                ) : (
                  <span className="text-[var(--placeholder-color)]" style={{ fontSize: "12px" }}>
                    QR data too large to display
                  </span>
                )}
              </div>
            )}
          </div>
        </Section>

        {irnDetails && (
          <>
            {/* ── TRANSACTION DETAILS ────────────────────────────────────────── */}
            <Section title="Transaction Details">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FieldItem label="Document Type Code" value={get("doc_typ", "docdtls_typ")} />
                <FieldItem label="Document Number" value={get("doc_no", "docdtls_no")} />
                <FieldItem label="Document Date" value={get("doc_date", "doc_dt", "docdtls_dt")} />
                <FieldItem label="Total Invoice Value" value={field("valdtls", "totinvval")} />
                <FieldItem label="Supply Type Code" value={field("trandtls", "suptyp")} />
                <FieldItem label="Reverse Charge" value={yesNo(field("trandtls", "regrev"))} />
                <FieldItem label="IGST on Intra-State" value={yesNo(field("trandtls", "igstonintra"))} />
              </div>
            </Section>

            {/* ── SELLER & BUYER DETAILS ─────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Section title="Organisation (Seller) Details">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldItem label="Supplier Legal Name" value={field("sellerdtls", "lglnm")} />
                  <FieldItem label="Supplier GSTIN" value={field("sellerdtls", "gstin")} />
                  <FieldItem
                    label="Supplier Address"
                    value={joinAddress(field("sellerdtls", "addr1"), field("sellerdtls", "addr2"))}
                  />
                  <FieldItem label="Supplier Place" value={field("sellerdtls", "loc")} />
                  <FieldItem label="Supplier State Code" value={field("sellerdtls", "stcd")} />
                  <FieldItem label="Supplier Pincode" value={field("sellerdtls", "pin")} />
                  {optional("Supplier Phone", field("sellerdtls", "ph"))}
                  {optional("Supplier Email", field("sellerdtls", "em"))}
                  {hasDisp && (
                    <>
                      <FieldItem label="Dispatch From – Name" value={field("dispdtls", "nm")} />
                      <FieldItem
                        label="Dispatch From – Address"
                        value={joinAddress(field("dispdtls", "addr1"), field("dispdtls", "addr2"))}
                      />
                      <FieldItem label="Dispatch From – Place" value={field("dispdtls", "loc")} />
                      <FieldItem label="Dispatch From – Pincode" value={field("dispdtls", "pin")} />
                      <FieldItem label="Dispatch From – State Code" value={field("dispdtls", "stcd")} />
                    </>
                  )}
                </div>
              </Section>

              <Section title="Customer (Buyer) Details">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FieldItem label="Recipient Legal Name" value={field("buyerdtls", "lglnm")} />
                  <FieldItem label="Recipient GSTIN" value={field("buyerdtls", "gstin")} />
                  <FieldItem
                    label="Recipient Address"
                    value={joinAddress(field("buyerdtls", "addr1"), field("buyerdtls", "addr2"))}
                  />
                  <FieldItem label="Recipient Place" value={field("buyerdtls", "loc")} />
                  <FieldItem label="Recipient State Code" value={field("buyerdtls", "stcd")} />
                  <FieldItem label="Recipient Pincode" value={field("buyerdtls", "pin")} />
                  <FieldItem label="Place of Supply State Code" value={field("buyerdtls", "pos")} />
                  {optional("Recipient Phone", field("buyerdtls", "ph"))}
                  {optional("Recipient Email", field("buyerdtls", "em"))}
                  {hasShip && (
                    <>
                      <FieldItem label="ShippingTo Legal Name" value={field("shipdtls", "lglnm")} />
                      <FieldItem label="ShippingTo GSTIN" value={field("shipdtls", "gstin")} />
                      <FieldItem
                        label="ShippingTo Address"
                        value={joinAddress(field("shipdtls", "addr1"), field("shipdtls", "addr2"))}
                      />
                      <FieldItem label="ShippingTo Place" value={field("shipdtls", "loc")} />
                      <FieldItem label="ShippingTo State Code" value={field("shipdtls", "stcd")} />
                      <FieldItem label="ShippingTo Pincode" value={field("shipdtls", "pin")} />
                    </>
                  )}
                </div>
              </Section>
            </div>

            {/* ── ITEM DETAILS ───────────────────────────────────────────────── */}
            {items.length > 0 && (
              <section className="border border-[var(--light-border-color)] rounded-[10px] overflow-hidden mb-4">
                <div className="px-5 py-3 bg-[var(--create-table-header-bg)] text-white">
                  <span className="font-[600] uppercase tracking-wide" style={{ fontSize: "12px" }}>
                    Item Details
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full" style={{ fontSize: "13px" }}>
                    <thead>
                      <tr className="bg-[var(--list-table-heading-bg)] text-[var(--list-table-heading-fg-color)]">
                        {["#", "Item Name", "HSN Code", "Qty", "Unit", "Unit Price", "Total Amount", "Discount", "Taxable Value", "GST Rate", "IGST", "CGST", "SGST", "Item Total", "Is Service"].map((h) => (
                          <th key={h} className="px-4 py-[10px] text-left font-[500] border-b border-[var(--light-border-color)] whitespace-nowrap" style={{ fontSize: "12px" }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((rawItem, idx) => {
                        const it = makeGetter(rawItem);
                        const cells = [
                          it("slno") ?? idx + 1,
                          it("prddesc"),
                          it("hsncd"),
                          it("qty"),
                          it("unit"),
                          it("unitprice"),
                          it("totamt"),
                          it("discount"),
                          it("assamt"),
                          it("gstrt") != null ? `${it("gstrt")}%` : null,
                          it("igstamt"),
                          it("cgstamt"),
                          it("sgstamt"),
                          it("totitemval"),
                          yesNo(it("isservc")),
                        ];
                        return (
                          <tr key={idx} className="border-b border-[var(--light-border-color)] hover:bg-[var(--row-hover-color)]">
                            {cells.map((c, i) => (
                              <td key={i} className={`px-4 py-3 text-[var(--secondary)] ${i === 1 ? "font-[500]" : ""}`}>
                                {c ?? "—"}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* ── VALUE DETAILS ──────────────────────────────────────────────── */}
            <Section title="Value Details">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <FieldItem label="Taxable Value" value={field("valdtls", "assval")} />
                <FieldItem label="IGST" value={field("valdtls", "igstval")} />
                <FieldItem label="CGST" value={field("valdtls", "cgstval")} />
                <FieldItem label="SGST" value={field("valdtls", "sgstval")} />
                <FieldItem label="Round Off" value={field("valdtls", "rndoffamt")} />
                <FieldItem label="Total Invoice Value" value={field("valdtls", "totinvval")} />
              </div>
            </Section>
          </>
        )}
    </>
  );
};

export default ViewEInvoice;
